import React from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ImageNotSupportedRoundedIcon from "@mui/icons-material/ImageNotSupportedRounded";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import { useNavigate } from "react-router-dom";
import { convertToIndianFormat } from "../../../utils/functions";
import { dateConverter } from "../../../utils/dateConveter";
import { calculatePercent } from "../../../utils/functions";

function getStatusConfig(status) {
  const value = String(status || "Frozen").toLowerCase();

  if (value.includes("complete")) {
    return {
      label: "Allocated",
      color: "#16A34A",
      bgcolor: alpha("#22C55E", 0.92),
    };
  }
  if (value.includes("pending")) {
    return {
      label: "Processing",
      color: "#D97706",
      bgcolor: alpha("#F59E0B", 0.92),
    };
  }
  return {
    label: "Frozen",
    color: "#2563EB",
    bgcolor: alpha("#3B82F6", 0.92),
  };
}

function FrozenPropertyCard({ row }) {
  const navigate = useNavigate();
  const status = getStatusConfig(row?._status);
  const lastEntry = row?.lastEntryAt
    ? dateConverter({
        value: row.lastEntryAt,
        momentIsTrue: true,
        dateFormat: "DD MMM YYYY",
      })
    : "-";
  const percentage = calculatePercent(row?.numberOfFracs - row?.remainingFracs, row?.numberOfFracs);
  const progress = Math.min(100, Math.max(0, percentage))
  const statusLabel = status?.label === "Allocated" ? "Allocation Completed" : "Allocation in progress";

  const handleViewDetails = () => {
    if (!row?.listingId) return;
    navigate(`/dashboard/property/${row.listingId}`, { state: { id: row.listingId } });
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderColor: "divider",
        transition: "transform .2s ease, box-shadow .2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: 4,
        },
      }}
    >
      <Box sx={{ position: "relative" }}>
        {row?._imageUrl ? (
          <Box
            component="img"
            src={row._imageUrl}
            alt={row?._title}
            loading="lazy"
            sx={{
              width: "100%",
              height: 200,
              objectFit: "cover",
              display: "block",
              bgcolor: "action.hover",
            }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: 200,
              display: "grid",
              placeItems: "center",
              bgcolor: "action.hover",
              color: "text.disabled",
            }}
          >
            <ImageNotSupportedRoundedIcon fontSize="large" />
          </Box>
        )}

        <Chip
          label={status.label}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            height: 28,
            fontWeight: 700,
            color: "#fff",
            bgcolor: status.bgcolor,
            backdropFilter: "blur(4px)",
          }}
        />
      </Box>

      <Box sx={{ p: 2, display: "flex", flexDirection: "column", flexGrow: 1 }}>
        <Typography
          title={row?._title}
          sx={{
            fontWeight: 800,
            fontSize: "1.05rem",
            // lineHeight: 1.3,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            // minHeight: "2.6em",
          }}
        >
          {row?._title || "Untitled listing"}
        </Typography>

        {row?._propertyType && row?._tenancyType && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {row._propertyType} - {row._tenancyType}
          </Typography>
        )}

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={2}
          sx={{ mt: 2 }}
        >
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                textTransform: "uppercase",
                letterSpacing: 0.8,
                fontWeight: 700,
                fontSize: 11,
              }}
            >
              Frozen amount
            </Typography>
            <Typography sx={{ fontWeight: 800, fontSize: "1.35rem", lineHeight: 1.2, mt: 0.25 }}>
              {convertToIndianFormat(row?.frozenAmount ?? 0)}
            </Typography>
          </Box>

          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: "text.secondary", pt: 0.5 }}>
            <CalendarMonthRoundedIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {lastEntry}
            </Typography>
          </Stack>
        </Stack>

          <Box sx={{ mt: 2,mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {statusLabel}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 800 }}>
                {progress}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 6,
                borderRadius: 999,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                "& .MuiLinearProgress-bar": {
                  borderRadius: 999,
                  bgcolor: "primary.main",
                },
              }}
            />
          </Box>

        <Button
          fullWidth
          variant="outlined"
          onClick={handleViewDetails}
          endIcon={<ChevronRightRoundedIcon />}
          sx={{
            mt: "auto",
            pt: 2,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 700,
            color: "text.primary",
            borderColor: "divider",
          }}
        >
          View Details
        </Button>
      </Box>
    </Card>
  );
}

export default function FrozenTab({ rows = [] }) {
  if (rows.length === 0) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            borderStyle: "dashed",
            py: 8,
            px: 3,
            textAlign: "center",
            bgcolor: (t) => alpha(t.palette.primary.main, 0.02),
          }}
        >
          <Stack spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                color: "primary.main",
              }}
            >
              <InboxRoundedIcon fontSize="medium" />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              No frozen amounts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Amounts committed to in-progress investments will appear here.
            </Typography>
          </Stack>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Grid container spacing={2.5}>
        {rows.map((row) => (
          <Grid item xs={12} sm={6} lg={4} key={row?.listingId || row?._id || JSON.stringify(row)}>
            <FrozenPropertyCard row={row} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
